/* eslint-disable react/jsx-no-useless-fragment */
import { FC, MouseEvent as ReactMouseEvent, useEffect, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { ActionIcon, Center, Skeleton, ThemeIcon, Tooltip } from '@mantine/core';
import { IconCheck, IconChevronDown, IconChevronDownLeft, IconChevronRight, IconPackage, IconPackgeExport, IconX } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import { i18n } from '@lingui/core';
import { useDebouncedCallback } from 'use-debounce';
import { Planning, ResourceType, Shortage, TableResponsiveProps } from '../../../../Types/ResourceT';
import IncDecCounter from '../../../../Atoms/inputs/IncDecCounter';
import ResourcePlanningS from '../../../../Service/restapi/resourcePlanningS';
import { ToastS } from '../../../../Service/ToastS';
import { openDeleteResourcePlanning } from '../../../../Templates/Resource/EventPlanningsModals';
import PlanningActions from './PlanningActions';

interface Props extends TableResponsiveProps {
  planning: Planning;
  depth?: number;
  refresh: () => unknown;
  showPlanningIcon?: boolean;
}

const PlanningTableRow: FC<Props> = ({
  planning: initialPlanning,
  depth = 0,
  refresh,
  showCategory = true,
  showType = true,
  showCounter = true,
  showPlanningIcon = true,
}) => {
  const [planning, setPlanning] = useState(initialPlanning);
  const { id: planningId, resource, plannedQuantity, children } = planning;
  const { id, type, name, content, category } = resource;
  const [opened, { toggle }] = useDisclosure(true);
  const navigate = useNavigate();
  const isItem = type === 'ITEM';

  useEffect(() => {
    setPlanning(initialPlanning);
  }, [initialPlanning]);

  const updateQuantity = useDebouncedCallback((quantity: number) => {
    ResourcePlanningS.updatePlanning(planningId, { plannedQuantity: quantity }).then((resJSON) => {
      if (resJSON) {
        setPlanning(resJSON);
        refresh();
      }
    });
  }, 1000);

  const onClick = () => navigate(`/resources/${id}/details`);

  const removePlanning = () => {
    const onConfirm = async () => {
      const deleted = await ResourcePlanningS.deletePlanning(planningId);
      if (deleted) {
        refresh?.();
      }
    };
    openDeleteResourcePlanning(name, onConfirm);
  };

  const mapSubRowUI = (_planning: Planning) => (
    <PlanningTableRow
      key={_planning.id}
      planning={_planning}
      depth={depth + 1}
      showCategory={showCategory}
      showType={showType}
      showCounter={showCounter}
      showPlanningIcon={showPlanningIcon && type !== 'CASE'}
      refresh={refresh}
    />
  );

  return (
    <>
      <tr>
        <td align="center" style={{ paddingLeft: depth * 10 }}>
          {!isItem && content.length > 0 && (
            <>
              {opened && <IconChevronDown size={20} onClick={toggle} style={{ cursor: 'pointer' }} />}
              {!opened && <IconChevronRight size={20} onClick={toggle} style={{ cursor: 'pointer' }} />}
            </>
          )}
          {isItem && depth > 0 && <IconChevronDownLeft size={20} />}
        </td>
        <td>
          {showPlanningIcon && (
            <Center>
              <PlanningStatusIcon planning={planning} refresh={refresh} onPlanningChanged={setPlanning} />
            </Center>
          )}
        </td>
        <td className="overflow-ellipsis" onClick={onClick} style={{ cursor: 'pointer' }}>
          {name}
        </td>
        {showCategory && (
          <td onClick={onClick} style={{ cursor: 'pointer', maxWidth: 100 }} className="overflow-ellipsis">
            {category?.name}
          </td>
        )}
        {showType && (
          <td onClick={onClick} style={{ cursor: 'pointer' }}>
            {i18n._(`resource.type.${type}`)}
          </td>
        )}
        {showCounter && (
          <td align="center">
            {depth === 0 && (
              <IncDecCounter initialCount={plannedQuantity} onChanged={updateQuantity} onLastRemoved={removePlanning} />
            )}
            {depth > 0 && initialPlanning.plannedQuantity}
          </td>
        )}
        <td style={{ position: 'relative' }}>
          {(depth === 0 || (showPlanningIcon && type !== 'CASE')) && (
            <Center>
              <div style={{ position: 'absolute', right: 0 }}>
                <PlanningActions planning={planning} refresh={refresh} removePlanning={removePlanning} />
              </div>
            </Center>
          )}
        </td>
      </tr>
      {children && children.length > 0 && opened && children.map(mapSubRowUI)}
    </>
  );
};

const PlanningStatusIcon: FC<{
  planning: Planning;
  refresh: () => unknown;
  onPlanningChanged: (planning: Planning) => void;
}> = ({ planning, refresh, onPlanningChanged }) => {
  const { planningGroup, warehousePlanning, resource, packedAt } = planning;
  const { entityId, entityType, begin, end } = planningGroup ?? {};
  const { id: resourceId, name } = resource;
  const [shortages, setShortages] = useState<Shortage[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingPackedAt, setUpdatingPackedAt] = useState(false);

  const updatePackedAt = async () => {
    if (updatingPackedAt) {
      return;
    }

    const packedAtValue = packedAt ? null : new Date();
    setUpdatingPackedAt(true);
    try {
      const resJSON = await ResourcePlanningS.updatePlanning(planning.id, { packedAt: packedAtValue });
      if (resJSON) {
        ToastS.info(
          `planning-packed-${planning.id}`,
          packedAtValue ? `Ressource ${name} wurde gepackt.` : `Ressource ${name} wurde entpackt.`,
        );
        onPlanningChanged(resJSON);
        refresh();
      }
    } finally {
      setUpdatingPackedAt(false);
    }
  };

  const packedIcon = (
    <Tooltip
      label={
        packedAt ? 'Resource ist gepackt. Klicken zum Entpacken.' : 'Resource ist nicht gepackt. Klicken zum Packen.'
      }
      position="bottom-start"
      transition="slide-down"
    >
      <ActionIcon
        aria-label={packedAt ? 'Resource entpacken' : 'Resource packen'}
        radius="xl"
        size="sm"
        color={packedAt ? 'blue' : 'gray'}
        variant={packedAt ? 'filled' : 'transparent'}
        onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          updatePackedAt();
        }}
        sx={{ flex: '0 0 auto' }}
      >
        {packedAt ? <IconPackage color='white' size={14} /> : <IconPackgeExport size={14} />}
      </ActionIcon>
    </Tooltip>
  );

  const fetchShortages = () => {
    setLoading(true);
    ResourcePlanningS.getShortages(resourceId, begin, end, entityType, entityId)
      .then(setShortages)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchShortages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planning]);

  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <Skeleton circle height={20} width={20} />
        {packedIcon}
      </div>
    );
  }

  if (warehousePlanning) {
    const warehouseShortage = shortages.reduce(
      (accumulator, currentShortage) =>
        accumulator ||
        (currentShortage.warehouseShortage < 0 && currentShortage.resourceType !== ResourceType.KIT) ||
        currentShortage.showWarehousePlanningShortage,
      false,
    );

    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <Tooltip
          label={
            warehouseShortage
              ? 'Resource ist bestätigt, aber es gibt einen Mangel.'
              : 'Resource ist eingeplant und ausreichend verfügbar'
          }
          position="bottom-start"
          transition="slide-down"
        >
          <div>
            {warehouseShortage ? (
              <ThemeIcon radius="xl" size="sm" color="#FF5C5A">
                <IconX size={14} />
              </ThemeIcon>
            ) : (
              <ThemeIcon radius="xl" size="sm" color="#3ac244">
                <IconCheck size={14} />
              </ThemeIcon>
            )}
          </div>
        </Tooltip>
        {packedIcon}
      </div>
    );
  }

  const shortage = shortages.reduce(
    (accumulator, currentShortage) =>
      accumulator ||
      (currentShortage.shortage < 0 && currentShortage.resourceType !== ResourceType.KIT) ||
      currentShortage.showResourcePlanningShortage,
    false,
  );

  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <Tooltip
        label={
          shortage
            ? 'Resource ist nicht ausreichend verfügbar.'
            : 'Resource ist verfügbar und muss noch bestätigt werden.'
        }
        position="bottom-start"
        transition="slide-down"
      >
        <ThemeIcon radius="xl" size="sm" color={shortage ? 'orange' : 'blue'}>
          {shortage && <IconX size={14} />}
          {!shortage && <IconCheck size={14} />}
        </ThemeIcon>
      </Tooltip>
      {packedIcon}
    </div>
  );
};

export default PlanningTableRow;
